import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NotificationToast } from '@/components/notifications/NotificationToast';
import { createNotificationMessage } from '@/utils/notificationUtils';

export interface Notification {
  id: string;
  message: string;
  timestamp: Date;
  read: boolean;
  type: 'device_enrolled' | 'device_removed';
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const handleDeviceChange = async (payload: any) => {
      if (payload.eventType === 'INSERT') {
        const { data: deviceData } = await supabase
          .from('devices')
          .select('os_type')
          .eq('id', payload.new.id)
          .single();

        const newNotification = createNotificationMessage('enrolled');
        addNotification(newNotification);

        toast({
          title: "New Device Enrolled",
          description: (
            <NotificationToast
              message={newNotification.message}
              osType={deviceData?.os_type}
              type="enrolled"
            />
          ),
          variant: 'default',
          duration: 5000,
        });
      } else if (payload.eventType === 'DELETE') {
        const newNotification = createNotificationMessage('removed');
        addNotification(newNotification);

        toast({
          title: "Device Removed",
          description: (
            <NotificationToast
              message={newNotification.message}
              type="removed"
            />
          ),
          variant: 'destructive',
          duration: 5000,
        });
      }
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupRealtime = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('NotificationProvider: no session, skipping realtime subscription');
        return;
      }
      channel = supabase
        .channel('schema-db-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'devices'
          },
          handleDeviceChange
        )
        .subscribe();
    };

    void setupRealtime();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}